import { useContext } from 'react';
import { ClickAwayListener, Tooltip, Typography } from '@mui/material';
import styled from 'styled-components';
import { Flex, FlexColumn } from '../Flex';
import Button from '../Button';
import { ReactComponent as GotoIcon } from '../../assets/16x16/goto.svg';
import { radius, shadow, space } from '../../style/utils';

const GotoIconStyled = styled(GotoIcon)`
  path {
    stroke: ${({ theme }) => theme.palette.icon.inverse};
  }
`;

const StyledTooltipContent = styled(FlexColumn)`
  gap: ${space('lg')};
  width: 280px;
  padding: ${space('lg')};
  background: ${({ theme }) => theme.palette.surface.base};
  color: ${({ theme }) => theme.palette.content.translucent.primary};
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${radius('lg')};
  box-shadow: ${shadow('md')};
`;

const TooltipContent = ({
  description,
  link,
}: {
  description?: string;
  link: string;
}) => (
  <StyledTooltipContent>
    <Typography fontSize={'14px'}>{description}</Typography>
    <Flex>
      <Button
        endIcon={<GotoIconStyled />}
        onClick={() => window.open(link, '_blank')}
      >
        Learn more
      </Button>
    </Flex>
  </StyledTooltipContent>
);

type Props = {
  description?: string;
  link: string;
  children: any;
  tooltipId: string;
  TooltipContext: React.Context<{
    openTooltip: string | null;
    setOpenTooltip: (id: string | null) => void;
  } | null>;
};

const AvailableInCloudTooltip = ({
  description,
  link,
  children,
  tooltipId,
  TooltipContext,
}: Props) => {
  const context = useContext(TooltipContext);
  if (!context)
    throw new Error(
      'AvailableInCloudTooltip must be used within TooltipContext',
    );

  const { openTooltip, setOpenTooltip } = context;
  const open = openTooltip === tooltipId;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenTooltip(open ? null : tooltipId);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenTooltip(null)}>
      <div>
        <Tooltip
          open={open}
          disableHoverListener
          disableTouchListener
          disableFocusListener
          placement="right"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                padding: 0,
              },
            },
          }}
          title={<TooltipContent description={description} link={link} />}
        >
          <div onClick={handleClick}>{children}</div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

export default AvailableInCloudTooltip;
